<template>
  <BaseModal
    :title="importSettings[current].title"
    description="Import Wizard."
    :is-open="showModal"
    @close="showModal = false"
  >
    <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
    <div
      class="drop-zone"
      :class="{ active: dragActive }"
      @dragenter.prevent="onDragEnter"
      @dragover.prevent
      @dragleave.prevent="onDragLeave"
      @drop.prevent="onDrop"
    >
      <div v-if="dragActive" class="drop-overlay">Drop file to import</div>
      <div>
        Refer to the
        <a
          href="https://help.piplanning.io/en/collections/603683-import-export"
          target="_blank"
          class="link"
        >
          documentation
        </a>
        to see how you need to format the file.
      </div>
      <p class="hint">Or drag and drop the file here.</p>
    </div>

    <p v-if="error" class="error">{{ error }}</p>

    <!-- eslint-disable-next-line vuejs-accessibility/form-control-has-label -->
    <input
      ref="fileInput"
      type="file"
      :accept="`.${importSettings[current].extension}`"
      class="hidden-file-input"
      @change="onFileSelected"
    />

    <template #actions>
      <BaseButton variant="ghost" color="grey" @click="showModal = false">Cancel</BaseButton>
      <BaseButton :disabled="hold" @click="$refs.fileInput.click()">
        Choose {{ importSettings[current].extension }} file
      </BaseButton>
    </template>
  </BaseModal>
</template>

<script>
import { mapState } from "pinia";

import { safepiplanningsImportModalSeen } from "@/analytics/events";
import { trackEvent } from "@/analytics/track";
import BaseButton from "@/components-ng/BaseButton.vue";
import BaseModal from "@/components-ng/BaseModal.vue";
import SessionHandler from "@/handlers/sessionHandler";
import { useAppStore } from "@/stores";
import { createHandlerContext } from "@/stores/context";
import { useToastStore } from "@/stores/toast";

export default {
  name: "ImportModal",
  components: { BaseModal, BaseButton },
  setup() {
    const toastStore = useToastStore();
    return { toastStore };
  },
  data() {
    return {
      importSettings: {
        backlog: {
          method: "import_csv",
          title: "CSV Import (Backlog Board)",
          extension: "csv",
        },
        team: {
          method: "import_team_csv",
          title: "CSV Import (Team Boards)",
          extension: "csv",
        },
        jalign: {
          method: "import_jalign_xlsx",
          title: "Jira Align xlsx import",
          extension: "xlsx",
        },
      },
      current: "backlog",
      csvSessionId: null,
      showModal: false,
      hold: false,
      error: "",
      dragDepth: 0,
      dragActive: false,
    };
  },
  computed: {
    ...mapState(useAppStore, ["company", "session"]),
  },
  methods: {
    showMessage(payload) {
      this.toastStore.showMessage(payload);
    },
    sessionHandler(sessionId) {
      return new SessionHandler(createHandlerContext(), sessionId);
    },
    show(session, type) {
      trackEvent(safepiplanningsImportModalSeen(type));

      this.error = "";
      this.csvSessionId = session;
      this.current = type;
      this.showModal = true;
    },
    onDragEnter() {
      this.dragDepth += 1;
      this.dragActive = true;
    },
    onDragLeave() {
      this.dragDepth = Math.max(0, this.dragDepth - 1);
      if (this.dragDepth === 0) this.dragActive = false;
    },
    onDrop(event) {
      this.dragDepth = 0;
      this.dragActive = false;
      const file = event.dataTransfer?.files?.[0];
      if (file) this.processFile(file);
    },
    onFileSelected(event) {
      const file = event.target.files?.[0];
      event.target.value = "";
      if (file) this.processFile(file);
    },
    processFile(file) {
      this.hold = true;
      const parts = file.name.split(".");
      const ext = parts[parts.length - 1].toLowerCase();
      const permittedExt = this.importSettings[this.current].extension;
      if (ext !== permittedExt) {
        this.fail(`File type must be ${permittedExt}, but was ${ext}.`);
        this.hold = false;
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        const contentBase64 = e.target.result;
        if (contentBase64.length > 500000) {
          this.fail("File is too big (> 400kB)");
          this.hold = false;
          return;
        }
        this.saveImportFile(contentBase64, file.name);
      };
      reader.readAsDataURL(file);
    },
    async saveImportFile(contentBase64, filename) {
      const method = this.importSettings[this.current].method;
      try {
        const res = await this.sessionHandler(this.csvSessionId).importFile(method, contentBase64);
        if (res.success) {
          this.showMessage({ message: `File ${filename} has been imported.` });
          this.showModal = false;
        } else {
          this.fail(res.error);
        }
      } catch (details) {
        this.fail(details);
      } finally {
        this.hold = false;
      }
    },
    fail(message) {
      this.error = message;
    },
  },
};
</script>

<style lang="scss" scoped>
@use "@/colors" as c;

.hidden-file-input {
  display: none;
}

.drop-zone {
  position: relative;
  padding: 16px;
  border: 1px dashed c.$border;
  border-radius: 8px;
  transition:
    border-color 0.15s ease,
    background-color 0.15s ease;

  &.active {
    border-color: c.$main;
    background-color: c.$background-grey-light;
  }
}

.drop-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  font-weight: 600;
  color: c.$main;
  background-color: rgb(255 255 255 / 85%);
  border-radius: 8px;
  pointer-events: none;
}

.hint {
  margin: 8px 0 0;
  font-size: 0.75rem;
  color: c.$text-subtle;
}

.error {
  margin-top: 12px;
  color: c.$red;
  font-size: 0.875rem;
}
</style>
